<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Question Bank Management
            </h1>
            <div class="flex flex-wrap items-center gap-3">
                <button @click="openImportModal" class="btn btn-success">
                    Import
                </button>
                <router-link
                    :to="{ name: 'QuestionBankCreate' }"
                    class="btn btn-primary"
                >
                    <Plus class="w-4 h-4 mr-1" />
                    Add Question
                </router-link>
            </div>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <!-- Search -->
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by question text, explanation, or tags..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <!-- Subject Filter -->
                    <SearchableSelect
                        v-model="filters.subject_id"
                        :options="subjectOptions"
                        placeholder="All Subjects"
                        option-label="name"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchQuestions"
                    />

                    <!-- Teacher Filter -->
                    <SearchableSelect
                        v-if="canViewTeacherFilter"
                        v-model="filters.teacher_id"
                        :options="teacherOptions"
                        placeholder="All Teachers"
                        :option-label="getTeacherName"
                        option-value="id"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchQuestions"
                    />

                    <!-- Type Filter -->
                    <SearchableSelect
                        v-model="filters.type"
                        :options="typeOptions"
                        placeholder="All Types"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchQuestions"
                    />

                    <!-- Difficulty Filter -->
                    <SearchableSelect
                        v-model="filters.difficulty"
                        :options="difficultyOptions"
                        placeholder="All Levels"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchQuestions"
                    />

                    <!-- Tags Filter -->
                    <input
                        v-model="filters.tags"
                        @input="debouncedSearch"
                        type="text"
                        placeholder="Search tags..."
                        class="flex-1 min-w-[140px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                    />

                    <!-- Status Filter -->
                    <SearchableSelect
                        v-model="filters.is_active"
                        :options="statusOptions"
                        placeholder="All Status"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchQuestions"
                    />

                    <button
                        @click="resetFilters()"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="questionBankStore.loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="8" />
        </div>

        <!-- Error State -->
        <div
            v-if="questionBankStore.error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ questionBankStore.error }}
                    </p>
                    <button
                        @click="questionBankStore.clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!questionBankStore.loading && !questionBankStore.error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Question
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Type
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Subject
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Teacher
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Difficulty
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Points
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Status
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="question in questionBankStore.questions"
                            :key="question.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div
                                    class="text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ question.question?.substring(0, 100)
                                    }}{{
                                        question.question?.length > 100
                                            ? "..."
                                            : ""
                                    }}
                                </div>
                                <div
                                    v-if="question.question?.length > 100"
                                    class="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs"
                                    :title="question.question"
                                >
                                    {{ question.question }}
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                <span
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                    :class="getTypeClass(question.type)"
                                >
                                    {{ question.type }}
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ question.subject?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ question.teacher?.user?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                <span
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                    :class="
                                        getDifficultyClass(question.difficulty)
                                    "
                                >
                                    {{ question.difficulty }}
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ question.points }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <span
                                    v-if="question.is_active"
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200"
                                >
                                    Active
                                </span>
                                <span
                                    v-else
                                    class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200"
                                >
                                    Inactive
                                </span>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="openDetailModal(question.id)"
                                    title="View Question"
                                    class="inline-flex items-center justify-center p-2 text-success-600 dark:text-success-400 hover:text-success-900 dark:hover:text-success-300 rounded-md hover:bg-success-50 dark:hover:bg-success-900/20 transition-colors mr-2"
                                >
                                    <Eye class="w-4 h-4" />
                                </button>
                                <router-link
                                    :to="{
                                        name: 'QuestionBankEdit',
                                        params: { id: question.id },
                                    }"
                                    title="Edit Question"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                >
                                    <Pencil class="w-4 h-4" />
                                </router-link>
                                <button
                                    @click="confirmDelete(question)"
                                    title="Delete Question"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Empty State -->
            <EmptyState
                v-if="questionBankStore.questions.length === 0"
                :icon="BookOpen"
                title="No questions found"
                description="Get started by adding a new question."
            >
                <router-link
                    :to="{ name: 'QuestionBankCreate' }"
                    class="btn btn-primary"
                >
                    <Plus class="w-4 h-4 mr-1" />
                    Add Question
                </router-link>
            </EmptyState>

            <!-- Pagination -->
            <BasePagination
                :pagination="questionBankStore.meta"
                :per-page="questionBankStore.perPage"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>

        <!-- Question Bank Import Modal -->
        <QuestionBankImportModal
            :showModal="showImportModal"
            @close="closeImportModal"
            @imported="handleImported"
        />

        <!-- Delete Confirmation Modal -->
        <DeleteConfirmationModal
            v-if="showDeleteModal"
            :show="showDeleteModal"
            :title="deleteModalTitle"
            :message="deleteModalMessage"
            @confirm="handleDelete"
            @close="closeDeleteModal"
        />

        <!-- Question Detail Modal -->
        <QuestionBankDetailModal
            :show="showDetailModal"
            :questionId="selectedQuestionId"
            @close="closeDetailModal"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from "vue";
import { useQuestionBankStore } from "@/stores/questionBankStore";
import { useAuthStore } from "@/stores/authStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import QuestionBankImportModal from "../components/QuestionBankImportModal.vue";
import QuestionBankDetailModal from "../components/QuestionBankDetailModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import NotificationService from "@/utils/notifications";
import subjectApi from "@/api/subject";
import teacherApi from "@/api/teacher";
import type { QuestionBank } from "@/types/questionBank";
import type { Subject } from "@/api/subject";
import type { Teacher } from "@/api/teacher";
import { Plus, RotateCcw, Pencil, Trash2, BookOpen, Eye } from "@lucide/vue";

const questionBankStore = useQuestionBankStore();
const authStore = useAuthStore();

// State
const searchQuery = ref("");
const filters = ref({
    subject_id: "", // Required field
    teacher_id: "", // Required field
    type: "", // Optional field
    difficulty: "", // Optional field
    tags: "", // Optional field
    search: "", // Optional field
    is_active: null as boolean | null, // Optional field
});
const showImportModal = ref(false);
const showDeleteModal = ref(false);
const showDetailModal = ref(false);
const selectedQuestion = ref<QuestionBank | null>(null);
const selectedQuestionId = ref<string | null>(null);

// Types for options with "Select All" option
type SubjectOption = Subject | { id: string; name: string };
type TeacherOption = Teacher | { id: ""; user: { name: "All Teachers" } };

const subjectOptions = ref<SubjectOption[]>([]);
const teacherOptions = ref<TeacherOption[]>([]);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this question? This action cannot be undone.",
);

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.subject_id !== "" ||
        filters.value.teacher_id !== "" ||
        filters.value.type !== "" ||
        filters.value.difficulty !== "" ||
        filters.value.tags !== "" ||
        filters.value.is_active !== null ||
        questionBankStore.page !== 1 ||
        questionBankStore.perPage !== 10
    );
});

// Options for Status filter
const statusOptions = [
    { value: null, label: "All Status" },
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
];

// Options for Type filter
const typeOptions = computed(() => [
    { value: "", label: "All Types" },
    { value: "multiple_choice", label: "Multiple Choice" },
    { value: "multiple_select", label: "Multiple Select" },
    { value: "true_false", label: "True/False" },
    { value: "essay", label: "Essay" },
]);

// Options for Difficulty filter
const difficultyOptions = computed(() => [
    { value: "", label: "All Levels" },
    { value: "easy", label: "Easy" },
    { value: "medium", label: "Medium" },
    { value: "hard", label: "Hard" },
]);

// Role-based visibility for Teacher filter
// Teacher filter should be visible for administrators and super-administrators only
const canViewTeacherFilter = computed(() => {
    return authStore.hasAnyRole(["administrator", "super-administrator"]);
});

// Methods
const handlePerPageChange = (value: number) => {
    questionBankStore.perPage = value;
    questionBankStore.page = 1;
    fetchQuestions();
};

const fetchQuestions = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await questionBankStore.fetchQuestions(searchFilters);
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value = {
        subject_id: "",
        teacher_id: "",
        type: "",
        difficulty: "",
        tags: "",
        search: "",
        is_active: null,
    };
    questionBankStore.setPage(1);
    questionBankStore.setPerPage(10);
    fetchQuestions();
};

const updateSubjectOptionsFromQuestions = () => {
    const seen = new Map<string, { id: string; name: string }>();
    questionBankStore.questions.forEach((q: QuestionBank) => {
        if (q.subject?.id && q.subject?.name && !seen.has(q.subject.id)) {
            seen.set(q.subject.id, { id: q.subject.id, name: q.subject.name });
        }
    });
    subjectOptions.value = [
        { id: "", name: "All Subjects" },
        ...Array.from(seen.values()),
    ];
};

const fetchOptions = async () => {
    if (!canViewTeacherFilter.value) {
        updateSubjectOptionsFromQuestions();
        teacherOptions.value = [{ id: "", user: { name: "All Teachers" } }];
        return;
    }

    try {
        const [subjectsResponse, teachersResponse] = await Promise.all([
            subjectApi.getAll({ per_page: 1000 }),
            teacherApi.getAll({ per_page: 1000 }),
        ]);
        subjectOptions.value = [
            { id: "", name: "All Subjects" },
            ...(subjectsResponse.data?.items || []),
        ];
        teacherOptions.value = [
            { id: "", user: { name: "All Teachers" } },
            ...(teachersResponse.data?.items || []),
        ];
    } catch {
        subjectOptions.value = [{ id: "", name: "All Subjects" }];
        teacherOptions.value = [{ id: "", user: { name: "All Teachers" } }];
    }
};

// For non-admin users, keep subject options in sync with loaded questions
watch(
    () => questionBankStore.questions,
    () => {
        if (!canViewTeacherFilter.value) {
            updateSubjectOptionsFromQuestions();
        }
    },
    { deep: true },
);

const debouncedSearch = useDebounce(fetchQuestions, 300);

const getTypeClass = (type: string) => {
    const typeClasses: Record<string, string> = {
        multiple_choice:
            "bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200",
        multiple_select:
            "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
        true_false:
            "bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200",
        essay: "bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200",
    };
    return (
        typeClasses[type] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
    );
};

const getDifficultyClass = (difficulty: string) => {
    const difficultyClasses: Record<string, string> = {
        easy: "bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200",
        medium: "bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200",
        hard: "bg-danger-100 text-danger-800 dark:bg-danger-900 dark:text-danger-200",
    };
    return (
        difficultyClasses[difficulty] ||
        "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
    );
};

const getTeacherName = (teacher: TeacherOption): string => {
    return teacher.user?.name || "";
};

const openImportModal = () => {
    showImportModal.value = true;
};

const closeImportModal = () => {
    showImportModal.value = false;
};

const handleImported = () => {
    showNotification("Questions imported successfully", "success");
    fetchQuestions();
};

const confirmDelete = (question: QuestionBank) => {
    deleteModalTitle.value = "Delete Question";
    deleteModalMessage.value = `Are you sure you want to delete "${question.question?.substring(0, 100)}${question.question?.length > 100 ? "..." : ""}"? This action cannot be undone.`;
    selectedQuestion.value = question; // Set the question to be deleted
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
};

const handleDelete = async () => {
    try {
        if (selectedQuestion.value) {
            await questionBankStore.deleteQuestion(selectedQuestion.value.id);
            showNotification("Question deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete question", "error");
    }
};

const openDetailModal = (questionId: string) => {
    selectedQuestionId.value = questionId;
    showDetailModal.value = true;
};

const closeDetailModal = () => {
    showDetailModal.value = false;
    selectedQuestionId.value = null;
};

const goToPage = (page: number) => {
    questionBankStore.setPage(page);
};

const showNotification = (
    message: string,
    type: "success" | "error" | "info" | "warning",
) => {
    switch (type) {
        case "success":
            NotificationService.success(message);
            break;
        case "error":
            NotificationService.error(message);
            break;
        case "info":
            NotificationService.info(message);
            break;
        case "warning":
            NotificationService.warning(message);
            break;
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchQuestions();
    fetchOptions();
});
</script>
